import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  ACCESS_KEY_PARAM,
  grantAccessSession,
  getEnvAccessKeyFallback,
  hasValidAccessSession,
  tryInheritAccessSessionFromOpener,
} from '../../utils/accessKey';
import {
  fetchAccessKeyGateStatus,
  verifyAccessKeyWithServer,
} from '../../services/accessKeyService';
import { isIndividualPoolSitePath } from '../monitoring/cctvPopupRoutes';

type Props = {
  children: React.ReactNode;
};

const AccessKeyGate: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [gateEnabled, setGateEnabled] = useState(false);
  const [allowed, setAllowed] = useState(false);

  const stripKeyFromUrl = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has(ACCESS_KEY_PARAM)) return;
    params.delete(ACCESS_KEY_PARAM);
    const rest = params.toString();
    const clean =
      location.pathname + (rest ? `?${rest}` : '') + (location.hash || '');
    window.history.replaceState(window.history.state, '', clean);
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setChecking(true);

      if (isIndividualPoolSitePath(location.pathname)) {
        setGateEnabled(false);
        setAllowed(true);
        setChecking(false);
        return;
      }

      const envKey = getEnvAccessKeyFallback();
      /** null = API 실패(알 수 없음) */
      let serverEnabled: boolean | null = null;
      try {
        const status = await fetchAccessKeyGateStatus();
        serverEnabled = Boolean(status.enabled);
      } catch {
        serverEnabled = null;
      }
      // 서버가 키 없음(false)일 때만 개방. API 실패 시에는 닫힘(다른 브라우저 무단 접속 방지).
      // DB에 키가 없어도 개발용 .env 키가 있으면 게이트 유지.
      const enabled =
        serverEnabled === true ||
        (serverEnabled === false && Boolean(envKey)) ||
        serverEnabled === null;

      if (cancelled) return;
      setGateEnabled(enabled);

      if (!enabled) {
        setAllowed(true);
        setChecking(false);
        return;
      }

      if (hasValidAccessSession()) {
        setAllowed(true);
        stripKeyFromUrl();
        setChecking(false);
        return;
      }

      if (tryInheritAccessSessionFromOpener()) {
        setAllowed(true);
        stripKeyFromUrl();
        setChecking(false);
        return;
      }

      const params = new URLSearchParams(location.search);
      const urlKey = params.get(ACCESS_KEY_PARAM);
      if (urlKey) {
        let valid = false;
        if (serverEnabled === true || serverEnabled === null) {
          try {
            valid = await verifyAccessKeyWithServer(urlKey);
          } catch {
            valid = Boolean(envKey && urlKey.trim() === envKey);
          }
        } else if (envKey) {
          valid = urlKey.trim() === envKey;
        }
        if (cancelled) return;
        if (valid) {
          grantAccessSession();
          setAllowed(true);
          stripKeyFromUrl();
          setChecking(false);
          return;
        }
      }

      setAllowed(false);
      setChecking(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);

  if (checking) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!gateEnabled || allowed) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
      }}
    >
      <Typography variant="h6" color="text.secondary" align="center">
        접근 권한 없음
      </Typography>
    </Box>
  );
};

export default AccessKeyGate;
